/**
 * Feature menu primitives and the global default-feature registry.
 *
 * Defines the wrappers structure modules use to compose their feature menus —
 * `SECTION` and `SUBSECTION` for grouping headings, `COMPOSITE` for a parent
 * feature that owns a set of sub-features, and `CHOOSEONE` for mutually
 * exclusive choices. The resulting `FeatureGroup` tree drives the admin feature
 * menu and feeds `assembleConfig` in `util/features.ts`.
 */

import type { Feature, Structure, TLabel } from 'util/stdtypes';

export type SectionFeature = {
    kind: 'section';
    label: TLabel;
    level?: number;
    features: FeatureMenuItem[];
};

export type CompositeFeature = {
    kind: 'composite';
    parent: Feature;
    features: FeatureMenuItem[];
};

export type ChooseOneFeature = {
    kind: 'chooseOne';
    label: TLabel | null;
    features: FeatureMenuItem[];
};

export function SECTION(label: TLabel, features: FeatureMenuItem[]): SectionFeature {
    return { kind: 'section', label, features };
}

export function SUBSECTION(label: TLabel, features: FeatureMenuItem[]): SectionFeature {
    return { kind: 'section', level: 2, label, features };
}

export function COMPOSITE(parent: Feature, features: FeatureMenuItem[]): CompositeFeature {
    return { kind: 'composite', parent, features };
}

export function CHOOSEONE(label: TLabel | null, features: FeatureMenuItem[]): ChooseOneFeature {
    return { kind: 'chooseOne', label, features };
}

export type FeatureGroup = SectionFeature | CompositeFeature | ChooseOneFeature;
export type FeatureMenuItem = FeatureGroup | Feature<any, any>;

/**
 * Return a copy of a structure with extra config-slot defaults merged in.
 *
 * The module-registration file is the one place allowed to import
 * `open-modules/`, so a partner widget that isn't behind a feature is wired
 * into the structure's config here rather than in the `structure/` file. Array
 * slots (byline tags, profile widgets, …) concatenate; scalar slots override.
 * The imported structure is left untouched.
 */
export function extendStructureConfig<T>(structure: Structure<T>, additions: Partial<T>): Structure<T> {
    const merged: Record<string, unknown> = { ...structure.receivesConfigDefaults };
    for (const [key, added] of Object.entries(additions)) {
        const existing = merged[key];
        merged[key] = Array.isArray(existing) && Array.isArray(added) ? [...existing, ...added] : added;
    }
    return { ...structure, receivesConfigDefaults: merged as T };
}
